import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { ReplayVideosEditor, newVideo } from "./ReplayVideosEditor";

const EMPTY = {
  titre: "", type: "webinaire", statut: "BROUILLON",
  date_debut: "", date_fin: "", description: "", intervenants: "",
  visuel: "", lien_billetterie_externe: "",
  replay_disponible: false, replay_videos: [], masque: false, filigrane_nominatif: false,
};

function toLocalInput(d) {
  if (!d) return "";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function toIso(local) {
  return local ? new Date(local).toISOString() : null;
}

export default function SeminarForm({ edit = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (edit && id) {
      api(`/admin/seminaires/${id}`).then((s) => {
        setStats(s.stats_videos || {});
        setForm({
          titre: s.titre || "", type: s.type || "webinaire", statut: s.statut || "BROUILLON",
          date_debut: toLocalInput(s.date_debut), date_fin: toLocalInput(s.date_fin),
          description: s.description || "", intervenants: (s.intervenants || []).join(", "),
          visuel: s.visuel || "", lien_billetterie_externe: s.lien_billetterie_externe || "",
          replay_disponible: !!s.replay_disponible,
          replay_videos: (s.replay_videos || []).map((v) => ({
            id: v.id, titre: v.titre || "", url: v.url || "", provider: v.provider || "vimeo",
            expire_le: toLocalInput(v.expire_le),
          })),
          masque: !!s.masque,
          filigrane_nominatif: !!s.filigrane_nominatif,
        });
      }).catch((e) => toast.error(e.message));
    }
  }, [edit, id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      titre: form.titre, type: form.type, statut: form.statut,
      date_debut: toIso(form.date_debut),
      date_fin: toIso(form.date_fin),
      description: form.description || null,
      intervenants: form.intervenants.split(",").map((s) => s.trim()).filter(Boolean),
      visuel: form.visuel || null,
      lien_billetterie_externe: form.lien_billetterie_externe || null,
      replay_disponible: form.replay_disponible,
      replay_videos: form.replay_videos.filter((v) => v.url.trim()).map((v) => ({
        id: v.id, titre: v.titre || null, url: v.url, provider: v.provider, expire_le: toIso(v.expire_le),
      })),
      masque: form.masque,
      filigrane_nominatif: form.filigrane_nominatif,
    };
    try {
      const res = edit
        ? await api(`/admin/seminaires/${id}`, { method: "PUT", body })
        : await api("/admin/seminaires", { method: "POST", body });
      toast.success(edit ? "Événement mis à jour" : "Événement créé");
      navigate("/admin/seminaires");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary";
  const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

  return (
    <AppLayout variant="admin">
      <div data-testid="seminar-form-page" className="max-w-3xl space-y-6 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">
          {edit ? "Modifier le événement" : "Nouveau événement"}
        </h1>

        <form onSubmit={submit} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5">
          <div>
            <label className={labelCls}>Titre *</label>
            <input data-testid="seminar-titre-input" required className={inputCls} value={form.titre} onChange={(e) => set("titre", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Type</label>
              <select data-testid="seminar-type-select" className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="webinaire">Webinaire</option>
                <option value="seminaire">Événement</option>
                <option value="congres">Congrès</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Statut</label>
              <select data-testid="seminar-statut-select" className={inputCls} value={form.statut} onChange={(e) => set("statut", e.target.value)}>
                <option value="BROUILLON">Brouillon</option>
                <option value="PUBLIE">Publié</option>
                <option value="ARCHIVE">Archivé</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Intervenants (virgules)</label>
              <input data-testid="seminar-intervenants-input" className={inputCls} value={form.intervenants} onChange={(e) => set("intervenants", e.target.value)} placeholder="Dr A, Pr B" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Début *</label>
              <input data-testid="seminar-date-debut-input" type="datetime-local" required className={inputCls} value={form.date_debut} onChange={(e) => set("date_debut", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Fin</label>
              <input data-testid="seminar-date-fin-input" type="datetime-local" className={inputCls} value={form.date_fin} onChange={(e) => set("date_fin", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea data-testid="seminar-description-input" rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Visuel (URL image)</label>
              <input data-testid="seminar-visuel-input" className={inputCls} value={form.visuel} onChange={(e) => set("visuel", e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <label className={labelCls}>Lien billetterie (HelloAsso)</label>
              <input data-testid="seminar-billetterie-input" className={inputCls} value={form.lien_billetterie_externe} onChange={(e) => set("lien_billetterie_externe", e.target.value)} placeholder="https://www.helloasso.com/…" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-cfhtb-text cursor-pointer rounded-md border border-slate-200 px-4 py-3">
            <input type="checkbox" data-testid="seminar-masque-checkbox" checked={form.masque}
              onChange={(e) => set("masque", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
            Masquer de l'extranet <span className="text-xs text-cfhtb-text/70">(invisible pour les adhérents : à venir, replays et historique — utile pour raisons juridiques)</span>
          </label>

          <fieldset className="rounded-md border border-slate-200 p-4 space-y-4">
            <legend className="text-sm font-medium text-cfhtb-heading px-1">Replay</legend>
            <label className="flex items-center gap-2 text-sm text-cfhtb-text cursor-pointer">
              <input type="checkbox" data-testid="seminar-replay-checkbox" checked={form.replay_disponible}
                onChange={(e) => {
                  set("replay_disponible", e.target.checked);
                  if (e.target.checked && form.replay_videos.length === 0) set("replay_videos", [newVideo()]);
                }}
                className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
              Replay disponible pour les inscrits
            </label>
            {form.replay_disponible && (
              <>
                <label className="flex items-center gap-2 text-sm text-cfhtb-text cursor-pointer">
                  <input type="checkbox" data-testid="seminar-filigrane-checkbox" checked={form.filigrane_nominatif}
                    onChange={(e) => set("filigrane_nominatif", e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                  Filigrane nominatif <span className="text-xs text-cfhtb-text/70">(nom et e-mail de l'adhérent affichés sur la vidéo pour décourager les partages)</span>
                </label>
                <ReplayVideosEditor videos={form.replay_videos} stats={stats} onChange={(v) => set("replay_videos", v)} />
              </>
            )}
          </fieldset>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" data-testid="seminar-cancel-button" onClick={() => navigate(-1)}
              className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors">
              Annuler
            </button>
            <button type="submit" data-testid="seminar-save-button" disabled={busy}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60">
              <Save className="h-4 w-4" /> {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
